import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { PetProvider } from "@/hooks/usePet";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { PageTransition } from "@/components/animations/PageTransition";
import React, { Suspense, useState, useCallback } from "react";
import { SplashScreen } from "@/components/SplashScreen";
import ProtectedRoute from "@/components/ProtectedRoute";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { safeGetItem, safeSetItem } from "@/lib/storage";
import type { SupportedLocale } from "@/i18n";

// Eager load critical path
import Landing from "./pages/Landing";
import AuthPage from "./pages/Auth";

// Lazy load everything else
const Onboarding = React.lazy(() => import("./pages/Onboarding"));
const Chat = React.lazy(() => import("./pages/Chat"));
const Settings = React.lazy(() => import("./pages/Settings"));
const PetProfile = React.lazy(() => import("./pages/PetProfile"));
const KidsReport = React.lazy(() => import("./pages/KidsReport"));
const TrialFinale = React.lazy(() => import("./pages/TrialFinale"));
const Garden = React.lazy(() => import("./pages/Garden"));
const BreedResults = React.lazy(() => import("./pages/BreedResults"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Journal = React.lazy(() => import("./pages/Journal"));
const Pricing = React.lazy(() => import("./pages/Pricing"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[100dvh] bg-chat-bg">
    <span className="text-4xl animate-pulse">🐾</span>
  </div>
);

/** Shared routes used inside each locale context */
const AppRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<LazyFallback />}>
        <Routes location={location} key={location.pathname}>
          {/* Only auth is public */}
          <Route path="auth" element={<PageTransition><AuthPage /></PageTransition>} />

          {/* Everything else requires login */}
          <Route index element={<ProtectedRoute><PageTransition><Landing /></PageTransition></ProtectedRoute>} />
          <Route path="garden" element={<ProtectedRoute><Garden /></ProtectedRoute>} />
          <Route path="pet/:petId" element={<ProtectedRoute><PetProfile /></ProtectedRoute>} />
          <Route path="pricing" element={<ProtectedRoute><PageTransition><Pricing /></PageTransition></ProtectedRoute>} />
          <Route path="onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute><PageTransition><Settings /></PageTransition></ProtectedRoute>} />
          <Route path="report/:petId" element={<ProtectedRoute><KidsReport /></ProtectedRoute>} />
          <Route path="finale/:petId" element={<ProtectedRoute><TrialFinale /></ProtectedRoute>} />
          <Route path="breeds/:petId" element={<ProtectedRoute><BreedResults /></ProtectedRoute>} />
          <Route path="journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute><PageTransition><NotFound /></PageTransition></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const AnimatedRoutes = () => {
  useKeyboardShortcuts();
  useScrollRestoration();
  useSessionTimeout();

  return (
    <>
      <Routes>
        {/* Russian locale */}
        <Route path="/ru/*" element={
          <LocaleProvider locale="ru">
            <AppRoutes />
          </LocaleProvider>
        } />
        {/* Spanish locale */}
        <Route path="/es/*" element={
          <LocaleProvider locale="es">
            <AppRoutes />
          </LocaleProvider>
        } />
        {/* English (default) — no prefix */}
        <Route path="/*" element={
          <LocaleProvider locale="en">
            <AppRoutes />
          </LocaleProvider>
        } />
      </Routes>
      <MobileBottomNav />
    </>
  );
};

const App = () => {
  const [showSplash, setShowSplash] = useState(() => {
    if (safeGetItem("peternity_splash_shown")) return false;
    return true;
  });

  const handleSplashComplete = useCallback(() => {
    safeSetItem("peternity_splash_shown", "1");
    setShowSplash(false);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AnimatePresence mode="wait">
          {showSplash && <SplashScreen key="splash" onComplete={handleSplashComplete} />}
        </AnimatePresence>
        {!showSplash && (
          <AuthProvider>
            <PetProvider>
              <BrowserRouter>
                <AnimatedRoutes />
              </BrowserRouter>
            </PetProvider>
          </AuthProvider>
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
